import { useEffect, useState, useRef, useMemo } from "react";
import { useProduct } from "../../context/ProductContext";
import "./Cate.css";
import { useLocalContext } from "../../context/LocalContext";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faShoppingBag,
  faShieldAlt,
  faCircleCheck,
  faBolt,
  faHeadset,
  faLock,
  faTag,
} from "@fortawesome/free-solid-svg-icons";
import Products from "../product/Products";

const Cate = ({ categoryMetadata, countdownActive, popupTimer }) => {
  const { products, selectedAntiv, cart, setCart, setProductToShow } =
    useProduct();
  const { webinfo, setShowCallModal } = useLocalContext();
  const navigate = useNavigate();
  const location = useLocation();
  const intervalRef = useRef(null);
  const timeoutRef = useRef(null);

  const brandName = selectedAntiv || "Security Suite";

  // Title (kept)
  useEffect(() => {
    const pretty =
      brandName && brandName !== "Security Suite" ? brandName : "Security";
    document.title = `${pretty} Plans | ${webinfo?.name || "Store"}`;
  }, [brandName, webinfo?.name]);

  // Optional call modal timer (kept behavior)
  const [callHelpTimer, setCallHelpTimer] = useState(2);
  const [callHelpCountdownActive, setCallHelpCountdownActive] = useState(false);

  useEffect(() => {
    if (location.pathname === "/call-help") {
      timeoutRef.current = setTimeout(() => {
        setCallHelpCountdownActive(true);
        let timeLeft = 2;
        setCallHelpTimer(timeLeft);

        intervalRef.current = setInterval(() => {
          timeLeft -= 1;
          setCallHelpTimer(timeLeft);
          if (timeLeft === 0) {
            clearInterval(intervalRef.current);
            setCallHelpCountdownActive(false);
            setShowCallModal(true);
          }
        }, 1000);
      }, 1500);
    }

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [location.pathname, setShowCallModal]);

  const selectedProducts = products?.[selectedAntiv] || [];

  const meta = categoryMetadata?.[selectedAntiv] || {};
  const title = meta?.title || `${brandName} protection plans`;
  const bannerImg = meta?.bannerImg || "";
  const whyChoose = meta?.whyChoose || "";
  const features = meta?.features || [];

  const formatPrice = (p) =>
    new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      maximumFractionDigits: 2,
    }).format(Number(p || 0));

  const short = (s = "", words = 24) => {
    const parts = String(s).trim().split(/\s+/);
    return parts.slice(0, words).join(" ") + (parts.length > words ? "…" : "");
  };

  const bestPick = useMemo(() => selectedProducts?.[0] || null, [selectedProducts]);

  if (!selectedProducts.length) {
    return (
      <div className="cate-empty">
        <div className="cate-empty-card">
          <h2>Plans are being prepared</h2>
          <p>
            We’re currently updating <strong>{brandName}</strong> plans. Please
            check back soon or explore other brands in the catalog.
          </p>
          <button className="cate-empty-btn" onClick={() => navigate("/latest-products")}>
            Browse all products
          </button>
        </div>
      </div>
    );
  }

  const handleNavigation = (value) => {
    setProductToShow(value);
    navigate(`${value.name.toLowerCase().replace(/\s+/g, "-")}`);
  };

  const handleCart = (item) => {
    if (!cart.some((bagItem) => bagItem.id === item.id)) {
      setCart((prevBag) => [...prevBag, item]);
      toast.success(`${item.name} added to cart`);
    } else {
      toast.info(`${item.name} is already in your cart`);
    }
  };

  return (
    <>
      {/* HERO */}
      <section className="cate-hero-v2" aria-labelledby="cate-hero-heading">
        <div className="cate-hero-surface" aria-hidden="true" />

        <div className="cate-hero-inner">
          <div className="cate-hero-copy">
            <div className="cate-kicker">
              <span className="cate-pill">
                <FontAwesomeIcon icon={faShieldAlt} /> {brandName} catalog
              </span>
              <span className="cate-note">
                Digital license • Instant delivery
              </span>
            </div>

            <h1 id="cate-hero-heading" className="cate-h1">
              Get protected without overpaying —{" "}
              <span className="cate-accent">{title}</span>
            </h1>

            <p className="cate-lead">
              {whyChoose ||
                `Compare plans based on devices, duration, and add-ons like VPN or identity protection. After checkout, you’ll get your license key by email along with a quick activation guide.`}
            </p>

            <div className="cate-bullets" role="list">
              <div className="cate-bullet" role="listitem">
                <FontAwesomeIcon icon={faCircleCheck} />
                <span>Key emailed fast + setup steps</span>
              </div>
              <div className="cate-bullet" role="listitem">
                <FontAwesomeIcon icon={faLock} />
                <span>Genuine license (trackable)</span>
              </div>
              <div className="cate-bullet" role="listitem">
                <FontAwesomeIcon icon={faHeadset} />
                <span>Call/chat guidance if stuck</span>
              </div>
            </div>

            <div className="cate-cta-row">
              {/* Popup countdown timer - shows when popup is about to open */}
              {countdownActive && popupTimer !== undefined && popupTimer > 0 && (
                <div className="cate-timer" role="status" aria-live="polite">
                  Call Us in <strong>{popupTimer}s</strong>…
                </div>
              )}

              {/* Original call-help timer - only for /call-help route */}
              {callHelpCountdownActive && location.pathname === "/call-help" && (
                <div className="cate-timer" role="status" aria-live="polite">
                  Connecting you in <strong>{callHelpTimer}s</strong>…
                </div>
              )}

              {webinfo?.phonecall ? (
                <a
                  href={`tel:${webinfo.phonecall}`}
                  className="cate-cta"
                  aria-label="Talk to an expert by phone"
                >
                  Talk to an expert {webinfo?.phone ? `(${webinfo.phone})` : ""}
                </a>
              ) : (
                <button
                  type="button"
                  className="cate-cta"
                  onClick={() => setShowCallModal(true)}
                >
                  Talk to an expert
                </button>
              )}
            </div>

            <div className="cate-trust">
              <span>
                <FontAwesomeIcon icon={faCircleCheck} /> No physical shipping
              </span>
              <span>
                <FontAwesomeIcon icon={faCircleCheck} /> Works after activation
              </span>
              <span>
                <FontAwesomeIcon icon={faCircleCheck} /> Renew anytime
              </span>
            </div>
          </div>

          <div className="cate-hero-art">
            <div className="cate-art-card">
              <div className="cate-art-top">
                <div className="cate-art-badge">
                  <FontAwesomeIcon icon={faBolt} /> Recommended
                </div>
                <div className="cate-art-sub">
                  Best for most users: 1–3 devices
                </div>
              </div>

              <div className="cate-art-visual">
                {bannerImg ? (
                  <img src={bannerImg} alt={`${brandName} plan illustration`} />
                ) : (
                  <div className="cate-art-fallback">
                    <p>{brandName}</p>
                    <span>Protection plans</span>
                  </div>
                )}
              </div>

              <div className="cate-art-mini">
                <p className="mini-title">
                  {bestPick?.name || `${brandName} popular plan`}
                </p>
                <p className="mini-desc">
                  {short(
                    bestPick?.desc ||
                      "A balanced plan for everyday browsing, work, streaming and safe payments.",
                    18
                  )}
                </p>

                <div className="mini-row">
                  <span className="mini-chip">
                    <FontAwesomeIcon icon={faTag} /> {bestPick?.license || "1 year"}
                  </span>
                  <span className="mini-price">
                    {formatPrice(bestPick?.price || 0)}
                    <small>/year</small>
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PLANS */}
      <section className="cate-plans" aria-labelledby="cate-plans-heading">
        <div className="cate-head">
          <h2 id="cate-plans-heading">Plans for {brandName}</h2>
          <p>
            Pick the device count first, then choose features you actually need.
            Your license key + quick-start guide will be emailed after checkout.
          </p>
        </div>

        <div className="cate-grid">
          {selectedProducts.map((product, idx) => {
            const inCart = cart.some((item) => item.id === product.id);

            const mrp = Number(product?.mrp || 0);
            const price = Number(product?.price || 0);
            const hasMrp = mrp > price && mrp > 0;
            const off = hasMrp ? Math.round(((mrp - price) / mrp) * 100) : 0;

            return (
              <article className={`cate-card ${idx === 0 ? "featured" : ""}`} key={product.id}>
                <div className="cate-card-top">
                  <span className="cate-card-pill">
                    {idx === 0 ? "Most picked" : "Popular"}
                  </span>

                  {hasMrp && off >= 10 && (
                    <span className="cate-save">
                      <FontAwesomeIcon icon={faTag} /> Save {off}%
                    </span>
                  )}
                </div>

                <button
                  className="cate-img"
                  onClick={() => handleNavigation(product)}
                  aria-label={`Open details for ${product.name}`}
                  type="button"
                >
                  <img src={product.img} alt={product.name} />
                </button>

                <button
                  className="cate-title"
                  onClick={() => handleNavigation(product)}
                  aria-label={`Open details for ${product.name}`}
                  type="button"
                >
                  {product.name}
                </button>

                <p className="cate-desc">
                  {short(
                    product.desc ||
                      "Everyday protection for browsing, streaming and work — with simple activation steps.",
                    26
                  )}
                </p>

                <div className="cate-chips">
                  <span className="cate-chip">{product.devices || "1–3 devices"}</span>
                  <span className="cate-chip">{product.duration || "1 year"}</span>
                  {product.license && (
                    <span className="cate-chip soft">{product.license}</span>
                  )}
                </div>

                <div className="cate-foot">
                  <div className="cate-price">
                    <div className="cate-price-main">
                      <span>{formatPrice(price)}</span>
                      <small>/year</small>
                    </div>

                    {hasMrp && (
                      <div className="cate-price-sub">
                        <span className="cate-mrp">{formatPrice(mrp)}</span>
                        <span className="cate-off">{off}% off</span>
                      </div>
                    )}
                  </div>

                  <button
                    type="button"
                    onClick={() => handleCart(product)}
                    className={`cate-btn ${inCart ? "in-cart" : ""}`}
                  >
                    {inCart ? (
                      "✔ Added"
                    ) : (
                      <>
                        <FontAwesomeIcon icon={faShoppingBag} /> Add
                      </>
                    )}
                  </button>
                </div>
              </article>
            );
          })}
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="cate-how" aria-labelledby="cate-how-heading">
        <div className="cate-head">
          <h2 id="cate-how-heading">How it works</h2>
          <p>Same process for all security brands sold here.</p>
        </div>

        <div className="cate-steps">
          {[
            {
              t: "Choose your plan",
              d: "Select device count and features you care about (VPN, identity, backups).",
            },
            {
              t: "Checkout securely",
              d: "Complete payment. Your order is confirmed instantly.",
            },
            {
              t: "Receive key by email",
              d: "We email your license key + quick-start activation steps.",
            },
          ].map((s, i) => (
            <div className="cate-step" key={i}>
              <div className="step-num">{i + 1}</div>
              <div className="step-body">
                <p className="step-title">{s.t}</p>
                <p className="step-desc">{s.d}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* FEATURES */}
      <section className="cate-feats" aria-labelledby="cate-feats-heading">
        <div className="cate-head">
          <h2 id="cate-feats-heading">Typical protection includes</h2>
          <p>
            Exact features vary by plan and OS, but most {brandName} editions cover:
          </p>
        </div>

        <div className="cate-feat-grid">
          {(features?.length ? features : [
            "Real-time malware & ransomware protection",
            "Web protection against unsafe sites and downloads",
            "Safer banking & shopping checks",
            "Optional VPN for public Wi-Fi",
            "Firewall / network protection (plan dependent)",
            "Performance and device tune-ups (plan dependent)",
          ])
            .slice(0, 12)
            .map((feat, i) => (
              <div className="cate-feat" key={i}>
                <span className="feat-dot">
                  <FontAwesomeIcon icon={faCircleCheck} />
                </span>
                <p>{String(feat).replace(/^✔\s*/, "")}</p>
              </div>
            ))}
        </div>
      </section>

      {/* FAQ */}
      <section className="cate-faq" aria-labelledby="cate-faq-heading">
        <div className="cate-head">
          <h2 id="cate-faq-heading">FAQ</h2>
          <p>Common questions for digital security licenses.</p>
        </div>

        <div className="cate-faq-grid">
          {[
            {
              q: "When will I receive the license key?",
              a: "Usually within minutes after payment. Occasionally longer if the payment gateway is slow.",
            },
            {
              q: "Can I activate on a new device later?",
              a: "Many plans allow moving devices as long as you stay within your device limit. Rules vary by vendor.",
            },
            {
              q: "Do you help with activation?",
              a: "Yes — we guide you through download + key activation. Deep in-app errors are handled by the vendor’s support.",
            },
            {
              q: "Is there auto-renewal?",
              a: "Most keys are time-bound. If a plan has auto-renewal, it should be clearly mentioned on the product page.",
            },
          ].map((item, idx) => (
            <details className="cate-faq-item" key={idx}>
              <summary>{item.q}</summary>
              <p>{item.a}</p>
            </details>
          ))}
        </div>
      </section>

      {/* Keep global listing */}
      <Products />
    </>
  );
};

export default Cate;
